import { z } from "zod";
// The analytics ingest and feature-flag routes (CHT-769, Phase 13.3). The apps send a client event and read client flags through the
// API. The apps hold no analytics vendor key and no vendor SDK: the server checks each event against the closed taxonomy
// (`@lafayette/domain`), scans it for forbidden data, reads the customer's opt-out, hashes the id, and only then hands it to the port.
//
// `CLIENT_ANALYTICS_EVENTS` must equal `analyticsClientEventNames` of the domain. A parity test in `apps/api` fails on a difference.
// `@lafayette/schemas` may not import the domain (package boundary), so the list is written here and tested there.
export const CLIENT_ANALYTICS_EVENTS = [
    "app_opened",
    "recommendation_viewed",
    "notification_opened",
    "admin_operation_performed",
    "session_started",
    "screen_viewed",
    "session_ended",
];
/**
 * One client event. The property values pass the taxonomy on the server; this schema only bounds the size and the type.
 * `occurredAt` (CHT-2028) is the time the event happened on the phone, as an ISO date-time. It is outside `properties`. The server accepts it only
 * inside `[received - 24 h, received + 60 s]` and refuses the event otherwise; without it the event has the server time.
 */
export const analyticsClientEventSchema = z.strictObject({
    event: z.enum(CLIENT_ANALYTICS_EVENTS),
    occurredAt: z.iso.datetime({ offset: true }).optional(),
    properties: z
        .record(z.string().min(1).max(64), z.union([z.string().max(64), z.number().int(), z.boolean()]))
        .refine((value) => Object.keys(value).length <= 16, "at most 16 properties")
        .default({}),
});
export const ANALYTICS_EVENTS_PER_REQUEST_MAX = 20;
export const analyticsEventsBodySchema = z.strictObject({
    events: z.array(analyticsClientEventSchema).min(1).max(ANALYTICS_EVENTS_PER_REQUEST_MAX),
});
export const analyticsViolationSchema = z.strictObject({
    code: z.enum([
        "UNKNOWN_EVENT",
        "UNKNOWN_PROPERTY",
        "MISSING_PROPERTY",
        "INVALID_VALUE",
        "NOT_FROM_THIS_SURFACE",
        "FORBIDDEN_DATA",
    ]),
    property: z.string().max(64).nullable(),
});
export const analyticsEventResultSchema = z.strictObject({
    status: z.enum(["sent", "disabled", "opted_out", "rejected", "failed"]),
    violations: z.array(analyticsViolationSchema).max(32).optional(),
});
/**
 * `disabled`: analytics is off on the server (the default). The app may stop sending until its next start. `processed`: each event has
 * a result. A rejected event is a defect of the app (a wrong property); an opted-out result means the customer chose no analytics.
 */
export const analyticsEventsResponseSchema = z.strictObject({
    status: z.enum(["disabled", "processed"]),
    results: z.array(analyticsEventResultSchema).max(ANALYTICS_EVENTS_PER_REQUEST_MAX),
});
/**
 * The client flags of the caller: flag key to variant name, and for each flag a `decisions` token that the server signed. An app hands
 * the token back to report what it showed. A server-only flag never appears.
 */
export const featureFlagsResponseSchema = z.strictObject({
    flags: z.record(z.string().min(1).max(64), z.string().min(1).max(32)),
    decisions: z.record(z.string().min(1).max(64), z.string().min(1).max(200)),
});
/**
 * The app says it showed a flag's variant by handing back the signed decision it got. The server reads the variant from the token (never
 * from a new decision, and never from the app), so a flag that changed since is recorded as it was shown.
 */
export const featureFlagExposureBodySchema = z.strictObject({
    flag: z.string().min(1).max(64),
    decision: z.string().min(1).max(200),
});
export const featureFlagExposureResponseSchema = z.strictObject({
    flag: z.string().min(1).max(64),
    variant: z.string().min(1).max(32),
});
/** The most that `visit_seq` may be: the same limit as the taxonomy, so the app never queues a number the server refuses. */
export const VISIT_SEQ_LIMIT = 100_000;
const SCREEN_NAMES = [
    "concierge",
    "trips",
    "trip_detail",
    "trip_new",
    "trip_edit",
    "itinerary_add",
    "itinerary_review",
    "itinerary_edit",
    "actions",
    "action_detail",
    "saved",
    "saved_item",
    "profile",
    "memory",
    "request_new",
    "request_answer",
    "sign_in",
    "other",
];
const ENTRY_VALUES = ["cold_start", "resume", "deep_link", "notification"];
const VISIT_ID = { kind: "entity_id" };
const VISIT_SEQ = { kind: "int", min: 0, max: VISIT_SEQ_LIMIT };
export const CLIENT_EVENT_SPECS = {
    app_opened: {
        required: ["entry"],
        properties: {
            entry: { kind: "enum", values: ENTRY_VALUES },
            visit_id: VISIT_ID,
            visit_seq: VISIT_SEQ,
        },
    },
    recommendation_viewed: {
        required: ["card_kind"],
        properties: {
            card_kind: {
                kind: "enum",
                values: ["catalog_item", "itinerary", "request_update", "action"],
            },
            catalog_item_id: { kind: "entity_id" },
            position_bucket: { kind: "enum", values: ["0", "1", "2_to_3", "4_to_9", "gte_10"] },
            visit_id: VISIT_ID,
            visit_seq: VISIT_SEQ,
        },
    },
    notification_opened: {
        required: ["topic"],
        properties: {
            topic: {
                kind: "enum",
                values: ["request_update", "handoff", "action_needed", "trip_inspiration", "product_news"],
            },
            visit_id: VISIT_ID,
            visit_seq: VISIT_SEQ,
        },
    },
    admin_operation_performed: {
        required: ["operation"],
        properties: {
            operation: {
                kind: "enum",
                values: [
                    "request_assigned",
                    "handoff_accepted",
                    "catalog_published",
                    "import_approved",
                    "memory_reviewed",
                    "itinerary_validated",
                ],
            },
            staff_role: {
                kind: "enum",
                values: ["concierge", "supervisor", "catalog_editor", "administrator"],
            },
        },
    },
    session_started: {
        required: ["visit_id", "visit_seq", "entry", "platform"],
        properties: {
            visit_id: VISIT_ID,
            visit_seq: VISIT_SEQ,
            entry: { kind: "enum", values: ENTRY_VALUES },
            platform: { kind: "enum", values: ["ios", "android"] },
        },
    },
    screen_viewed: {
        required: ["visit_id", "visit_seq", "screen", "previous"],
        properties: {
            visit_id: VISIT_ID,
            visit_seq: VISIT_SEQ,
            screen: { kind: "enum", values: SCREEN_NAMES },
            previous: { kind: "enum", values: ["none", ...SCREEN_NAMES] },
        },
    },
    session_ended: {
        required: ["visit_id", "visit_seq", "end_reason", "duration_bucket", "screens_bucket"],
        properties: {
            visit_id: VISIT_ID,
            visit_seq: VISIT_SEQ,
            end_reason: { kind: "enum", values: ["background", "idle_timeout", "sign_out"] },
            duration_bucket: {
                kind: "enum",
                values: ["lt_1m", "1m_to_5m", "5m_to_30m", "30m_to_4h", "gte_4h"],
            },
            screens_bucket: { kind: "enum", values: ["0", "1", "2_to_3", "4_to_9", "gte_10"] },
        },
    },
};
const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
function clientValueFits(property, value) {
    switch (property.kind) {
        case "enum":
            return typeof value === "string" && property.values.includes(value);
        case "entity_id":
            return typeof value === "string" && UUID_SHAPE.test(value);
        case "int":
            return (typeof value === "number" &&
                Number.isInteger(value) &&
                value >= property.min &&
                value <= property.max);
    }
}
/**
 * Checks one client event against the closed contract. `true` only when the event is a client event, every property is listed with a
 * value of its kind, and every required property is present. A made-up key, a text, a number in an enum or a missing property is `false`.
 * Nothing is repeated: the answer is a boolean.
 */
export function isValidClientAnalyticsEvent(event, properties) {
    if (!Object.hasOwn(CLIENT_EVENT_SPECS, event))
        return false;
    const spec = CLIENT_EVENT_SPECS[event];
    for (const [key, value] of Object.entries(properties)) {
        if (value === undefined)
            continue;
        const property = Object.hasOwn(spec.properties, key) ? spec.properties[key] : undefined;
        if (property === undefined || !clientValueFits(property, value))
            return false;
    }
    // The visit id and the sequence go together (the server refuses an event with only one of them, so the phone never queues it).
    if ((properties.visit_id === undefined) !== (properties.visit_seq === undefined))
        return false;
    return spec.required.every((key) => properties[key] !== undefined);
}
// --- the customer's analytics choice ---------------------------------------------------------------------------------------------
/** `optedOut` true: no product analytics event and no vendor flag request for this customer. A customer who never chose reads `false`. */
export const customerAnalyticsPreferenceSchema = z.strictObject({ optedOut: z.boolean() });
export const customerAnalyticsPreferenceBodySchema = z.strictObject({ optedOut: z.boolean() });
